import _ from 'lodash'
import { AnyObject } from '@feedyou/utils'
import { Dictionary } from '@feedyou-ai/feedbot-schema'

import {
  FeatureAvailabilities,
  FeatureDefinition,
  FeatureDefinitions,
  FeatureName,
  FeaturesByAvailability,
  FeaturesSchema,
  FeatureValues
} from '../../../../store/features/types'
import { FeatureAvailability, featuresSchema } from '../../../../store/features/featuresSchema'
import { sortObjectKeys } from '../../../../lib/object/sortObjectKeys'
import { groupDictBy } from '../../../../lib/object/groupDictBy'

type FormSchema = AnyObject

export function getSchema(
  availabilities: FeatureAvailabilities,
  allowedSections: Record<FeatureAvailability, boolean>
): FormSchema {
  const featureSchemaByAvailability = groupDictBy(
    featuresSchema, // I <3 PRETTIER
    (feature, name) => availabilities[name]
  )

  function onlyKeepAllowedSections(obj: Dictionary<FeatureAvailability, any>) {
    return _.pickBy(obj, (_, key) => allowedSections[key as FeatureAvailability])
  }

  return {
    $id: 'features-schema-id',
    type: 'object',
    properties: onlyKeepAllowedSections({
      [FeatureAvailability.PUBLIC_BETA]: {
        $id: 'public-features-schema-id',
        type: 'object',
        title: 'Public beta features',

        properties: featureDefinitionsToRjsfProperties(
          featureSchemaByAvailability[FeatureAvailability.PUBLIC_BETA]
        )
      },
      [FeatureAvailability.PRIVATE_BETA]: {
        $id: 'private-features-schema-id',
        type: 'object',
        title: 'Private beta features ',
        description: 'Feedyou only',

        properties: featureDefinitionsToRjsfProperties(
          featureSchemaByAvailability[FeatureAvailability.PRIVATE_BETA]
        )
      },
      [FeatureAvailability.DEVELOPMENT]: {
        $id: 'development-features-schema-id',
        type: 'object',
        title: 'Beta features under development',
        description: 'Features under developement are visible for Engineering & Product team only',

        properties: featureDefinitionsToRjsfProperties(
          featureSchemaByAvailability[FeatureAvailability.DEVELOPMENT]
        )
      },
      [FeatureAvailability.HIDDEN]: {
        $id: 'hidden-features-schema-id',
        type: 'object',
        title: 'Hidden beta features',
        description: 'Hidden features are visible for Engineering & Product team only',

        properties: featureDefinitionsToRjsfProperties(
          featureSchemaByAvailability[FeatureAvailability.HIDDEN]
        )
      }
    })
  }
}

const featureDefinitionsToRjsfProperties = (dictionary: Partial<FeatureDefinitions> = {}) => {
  const sortedDefinitions = sortObjectKeys(dictionary, (key1: FeatureName, key2: FeatureName) => {
    const title1 = dictionary[key1]!.title
    const title2 = dictionary[key2]!.title

    return title1?.localeCompare(title2)
  })

  return _.mapValues(
    sortedDefinitions, // I <3 PRETTIER
    (value: FeatureDefinition) => featureDefinitionToRjsfSchema(value)
  )
}

const featureDefinitionToRjsfSchema = (definition: FeatureDefinition): AnyObject => {
  return {
    type: definition.type,
    title: definition.title,
    enum: definition.enum,
    description: definition.description
  }
}

export function getFormData(
  features: FeatureValues,
  featuresSchema: FeaturesSchema,
  availabilities: FeatureAvailabilities
): FeaturesByAvailability {
  const schemaDefinitionsByAvailability = groupDictBy(
    featuresSchema,
    (_, name) => availabilities[name]
  )

  return _.mapValues(
    schemaDefinitionsByAvailability,
    (definitionsForAvailability: FeatureDefinitions) => {
      return _.mapValues(
        definitionsForAvailability,
        (definition: FeatureDefinition, name: FeatureName) => features[name]!
      )
    }
  )
}

const propertiesUiSchema = _.mapValues(featuresSchema, () => ({
  'ui:widget': 'RadioWithDefaultWidget',
  'ui:classNames': 'rjsf-horizontal-radio-group'
}))

export const uiSchema = Object.fromEntries(
  Object.values(FeatureAvailability).map(availability => [availability, propertiesUiSchema])
)
